"use client";

import { FC, useActionState, useEffect, useRef } from "react";

import { MODALS } from "@Shared/config";
import { useModalState } from "@Shared/store";
import { ElProps } from "@Shared/types";
import { ButtonSubmit, Input, Text } from "@Shared/ui";
import { twcx } from "@Shared/utils";

import { sendForm } from "../actions";
import { FormStateModel } from "../models";

const initialState: FormStateModel = {
    success: false,
    error: null,
};

export type FormProps = ElProps<"div">;

export const Form: FC<FormProps> = ({ className, ...restProps }) => {
    const refForm = useRef<HTMLFormElement>(null);

    const [formState, formAction] = useActionState(sendForm, initialState);
    const { open: modalOpen } = useModalState(["open"]);

    useEffect(() => {
        if (formState.error?.fields) {
            (
                refForm.current?.querySelector(`[name="${Object.keys(formState.error.fields)[0]}"]`) as HTMLElement
            )?.focus();
        }

        if (formState.success) {
            refForm.current?.reset();

            modalOpen(MODALS.info, {
                title: "Thank you!",
                text: "Your submission has been received!",
            });
        }
    }, [formState]);

    return (
        <div
            className={twcx(
                "col-span-full flex flex-col gap-5",
                "md:col-span-2",
                "lg:col-span-6 lg:-col-end-1",
                "xl:col-span-5 xl:-col-end-1 xl:pl-10",
                className
            )}
            {...restProps}
        >
            <Text as="h4" variant="h4" weight={600}>
                Still have questions?
            </Text>

            <form ref={refForm} className={twcx("flex flex-col gap-3", "lg:flex-row")} action={formAction}>
                <Input
                    className="grow"
                    classNames={{
                        error: "lg:absolute lg:left-0 lg:top-full lg:mt-2",
                    }}
                    id="fb-email"
                    type="email"
                    name="email"
                    defaultValue={formState.defaultValues?.email}
                    placeholder="Enter your email"
                    required
                    error={formState.error?.fields?.email}
                />

                <ButtonSubmit>Send</ButtonSubmit>

                {formState.error?.message && (
                    <Text className="lg:absolute lg:left-0 lg:top-full lg:mt-2" variant="body-small" color="error-2">
                        {formState.error.message}
                    </Text>
                )}
            </form>
        </div>
    );
};
